import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Stethoscope, Droplets, HeartPulse, Activity, Thermometer, Wind,
  Fingerprint, Scale, Bandage, Scissors, Phone,
  CalendarDays, MapPin, Menu, X, ShieldCheck, ClipboardList,
  UserRound, PhoneCall, Clock, CheckCircle2, Languages, Building2, Star
} from 'lucide-react';

const PHONE = '086085 95239';
const PHONE_TEL = 'tel:08608595239';
const DIRECTIONS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('K.M. Multi Speciality Clinic Koliyanur Tamil Nadu 605103');
const MAPS_LISTING_URL = 'https://www.google.com/maps?cid=5931432580425859079';
const MEDWAY_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Medway Hospital Viluppuram Tamil Nadu');
const MGMCRI_MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Mahatma Gandhi Medical College and Research Institute Puducherry');
const ADDRESS_EN = '3/114, Bus Stop, E Pondy Road, Opp. to BDO Office, Koliyanur, Tamil Nadu – 605103';
const ADDRESS_TA = '3/114, பேருந்து நிறுத்தம், கிழக்கு பாண்டி சாலை, BDO அலுவலகம் எதிரில், கோலியனூர், தமிழ்நாடு – 605103';
const LOGO = '/assets/clinic/logo.png';
const DOCTOR_PHOTO = '/assets/clinic/doctor.jpg';

const NAV_LINKS = [
  { id: 'home', en: 'Home' },
  { id: 'about', en: 'About' },
  { id: 'services', en: 'Services' },
  { id: 'doctor', en: 'Doctor' },
  { id: 'contact', en: 'Contact' },
];

const Tam = ({ children, className = '' }) => (
  <span lang="ta" className={`font-tamil ${className}`}>{children}</span>
);

/* ---------------- Header ---------------- */
const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-white/95 backdrop-blur ${scrolled ? 'shadow-[0_2px_20px_rgba(22,52,107,0.10)]' : ''}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Brand */}
          <button onClick={() => go('home')} data-testid="brand-home-link" className="flex items-center gap-2.5 text-left">
            <img src={LOGO} alt="K.M. Multi Speciality Clinic logo" className="h-10 w-10 md:h-11 md:w-11 object-contain" />
            <div className="leading-tight">
              <p className="font-bold text-navy text-[13px] sm:text-sm tracking-tight">K.M. MULTI SPECIALITY CLINIC</p>
              <p className="font-tamil text-green-700 text-[11px] sm:text-xs">K.M. சிறப்பு சிகிச்சை மையம்</p>
            </div>
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7" aria-label="Primary">
            {NAV_LINKS.map(l => (
              <button key={l.id} data-testid={`nav-${l.id}`} onClick={() => go(l.id)}
                className="text-sm font-medium text-slate-600 hover:text-navy transition-colors">
                {l.en}
              </button>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a href={PHONE_TEL} data-testid="header-call-now"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-green-700 transition-colors">
              <PhoneCall className="h-4 w-4" /> Call Now
            </a>
            <a href={PHONE_TEL} data-testid="header-book-appointment"
              className="inline-flex items-center gap-2 rounded-full bg-green-700 hover:bg-green-800 text-white text-sm font-semibold px-5 py-2.5 shadow-sm transition-colors">
              <CalendarDays className="h-4 w-4" /> Book Appointment
            </a>
          </div>

          {/* Mobile hamburger */}
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Menu"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-navy hover:bg-slate-100">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-slate-100 bg-white shadow-lg">
          <div className="px-4 py-3 space-y-1">
            {NAV_LINKS.map(l => (
              <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => go(l.id)}
                className="block w-full text-left px-3 py-3 rounded-lg text-[15px] font-medium text-slate-700 hover:bg-slate-50">
                {l.en}
              </button>
            ))}
            <div className="flex gap-2 pt-2 pb-1">
              <a href={PHONE_TEL} data-testid="mobile-call-now"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-navy text-navy font-semibold py-3">
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a href={PHONE_TEL} data-testid="mobile-book-appointment"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-green-700 text-white font-semibold py-3">
                <CalendarDays className="h-4 w-4" /> Book
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => (
  <section id="home" data-testid="hero-section" className="pt-24 md:pt-32 pb-14 md:pb-20 bg-white overflow-hidden">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
        {/* Left */}
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-navy/5 border border-navy/10 text-navy text-[11px] font-bold tracking-[0.18em] px-4 py-1.5">
            K.M. MULTI SPECIALITY CLINIC
          </p>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold text-navy leading-[1.12] tracking-tight">
            Comprehensive Medical Care for You &amp; Your Family
          </h1>
          <Tam className="block mt-3 text-lg sm:text-xl text-green-700 font-semibold leading-relaxed">
            உங்களுக்கும் உங்கள் குடும்பத்திற்கும் முழுமையான மருத்துவ சேவை
          </Tam>
          <p className="mt-5 text-slate-600 text-base leading-relaxed max-w-lg">
            Trusted, doctor-led medical care for common illnesses, chronic diseases, preventive healthcare and selected minor procedures.
          </p>
          <Tam className="block mt-2 text-slate-500 text-sm leading-relaxed max-w-lg">
            பொதுவான நோய்கள், நீண்டகால நோய்கள், தடுப்பு மருத்துவம் மற்றும் தேர்ந்தெடுக்கப்பட்ட சிறிய மருத்துவச் சிகிச்சைகளுக்கான நம்பகமான மருத்துவர் வழிநடத்தும் மருத்துவ சேவை.
          </Tam>

          {/* Doctor chip */}
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 max-w-lg">
            <UserRound className="h-5 w-5 text-navy mt-0.5 shrink-0" />
            <div>
              <p className="font-bold text-navy text-[15px]">Dr. K. Mouhamed Nazar, MBBS., MD (General Medicine), M.Diab.</p>
              <p className="text-slate-600 text-sm">Assistant Professor, Dept. of General Medicine</p>
              <p className="text-slate-500 text-[12px]">Mahatma Gandhi Medical College and Research Institute, Puducherry</p>
              <Tam className="block text-slate-500 text-[13px] mt-1 leading-snug">
                மருத்துவர் கே. முகமது நாசர், MD (பொது மருத்துவம்) — பொது மருத்துவத் துறை உதவிப் பேராசிரியர்
              </Tam>
            </div>
          </div>

          {/* Tagline */}
          <p className="mt-5 text-green-700 font-semibold text-sm tracking-wide">
            Care Beyond Treatment <span className="text-slate-300 mx-1">|</span> <Tam>சிகிச்சையுடன், உங்கள் முழுமையான நலனிலும் அக்கறை</Tam>
          </p>

          {/* CTAs */}
          <div className="mt-7 flex flex-wrap gap-3">
            <a href={PHONE_TEL} data-testid="hero-book-appointment"
              className="inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-dark text-white font-semibold px-7 py-3.5 shadow-md shadow-navy/20 transition-colors">
              <CalendarDays className="h-5 w-5" /> Book an Appointment
            </a>
            <a href={PHONE_TEL} data-testid="hero-call-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-green-700 text-green-700 hover:bg-green-50 font-semibold px-7 py-3.5 transition-colors">
              <Phone className="h-5 w-5" /> Call {PHONE}
            </a>
          </div>
        </div>

        {/* Right — doctor visual */}
        <div className="relative">
          <div className="absolute -top-6 -right-6 h-40 w-40 rounded-full bg-green-100/70 blur-2xl" aria-hidden="true" />
          <div className="absolute -bottom-8 -left-8 h-48 w-48 rounded-full bg-navy/5 blur-2xl" aria-hidden="true" />
          <div className="relative rounded-[28px] bg-gradient-to-b from-slate-50 to-slate-100 border border-slate-200/80 p-5 sm:p-7 shadow-[0_20px_50px_rgba(22,52,107,0.10)]">
            <img
              src={DOCTOR_PHOTO}
              alt="Dr. K. Mouhamed Nazar, MD (General Medicine) — K.M. Multi Speciality Clinic, Koliyanur"
              data-testid="hero-doctor-photo"
              className="w-full max-w-[340px] mx-auto rounded-2xl object-cover object-top shadow-lg"
            />
            <div className="mt-4 rounded-2xl bg-white border border-slate-100 shadow-sm px-5 py-3.5 text-center">
              <p className="font-bold text-navy">Dr. K. Mouhamed Nazar</p>
              <p className="text-xs text-slate-500 mt-0.5">MBBS., MD (Gen. Med.), M.Diab. · Consultant Physician</p>
              <p className="text-xs text-green-700 font-semibold mt-1 flex items-center justify-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Mon – Sat · 6:00 PM – 10:30 PM
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Trust strip ---------------- */
const TRUST = [
  { en: 'Doctor-Led Care', ta: 'மருத்துவர் நேரடி மருத்துவ சேவை', Icon: Stethoscope },
  { en: 'Comprehensive Evaluation', ta: 'முழுமையான மருத்துவ மதிப்பீடு', Icon: ClipboardList },
  { en: 'Evidence-Based Treatment', ta: 'அறிவியல் அடிப்படையிலான சிகிச்சை', Icon: ShieldCheck },
  { en: 'Long-Term Follow-up', ta: 'தொடர்ச்சியான நோய் கண்காணிப்பு', Icon: HeartPulse },
];

const TrustStrip = () => (
  <section data-testid="trust-strip" className="border-y border-slate-100 bg-slate-50/70">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {TRUST.map(({ en, ta, Icon }, i) => (
          <div key={en} data-testid={`trust-item-${i}`} className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
              <Icon className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <p className="font-semibold text-navy text-sm leading-snug">{en}</p>
              <Tam className="block text-slate-500 text-xs mt-1 leading-snug">{ta}</Tam>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- About ---------------- */
const About = () => (
  <section id="about" data-testid="about-section" className="py-16 md:py-24 bg-white scroll-mt-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
      <div className="order-2 md:order-1 relative">
        <div className="rounded-[28px] overflow-hidden border border-slate-200 bg-slate-50 p-6 sm:p-8 shadow-sm">
          <img src={LOGO} alt="K.M. Multi Speciality Clinic — கோலியனூர்" data-testid="about-clinic-logo"
            className="w-40 h-40 sm:w-52 sm:h-52 object-contain mx-auto" />
          <div className="mt-5 grid grid-cols-3 gap-3 text-center">
            {[
              { en: 'Accurate Diagnosis', ta: 'துல்லிய நோயறிதல்' },
              { en: 'Evidence-Based Care', ta: 'அறிவியல் சிகிச்சை' },
              { en: 'Family First', ta: 'குடும்ப முன்னுரிமை' },
            ].map(x => (
              <div key={x.en} className="rounded-xl bg-white border border-slate-100 px-2 py-3 shadow-sm">
                <CheckCircle2 className="h-4 w-4 text-green-700 mx-auto mb-1.5" />
                <p className="text-[11px] font-semibold text-navy leading-tight">{x.en}</p>
                <Tam className="block text-[10px] text-slate-500 mt-0.5 leading-tight">{x.ta}</Tam>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="order-1 md:order-2">
        <p className="text-green-700 font-bold text-xs tracking-[0.18em] uppercase">About the Clinic</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-navy tracking-tight leading-tight">
          Trusted Medical Care, Close to Home
        </h2>
        <Tam className="block mt-2 text-lg text-green-700 font-semibold">
          உங்கள் அருகிலேயே நம்பகமான மருத்துவ சேவை
        </Tam>
        <p className="mt-5 text-slate-600 leading-relaxed">
          K.M. Multi Speciality Clinic provides comprehensive medical care for individuals and families, with a focus on accurate diagnosis, evidence-based treatment and long-term health management.
        </p>
        <Tam className="block mt-3 text-slate-500 leading-relaxed text-[15px]">
          K.M. Multi Speciality Clinic தனிநபர்கள் மற்றும் குடும்பங்களுக்கான முழுமையான மருத்துவ சேவையை வழங்குகிறது. துல்லியமான நோயறிதல், அறிவியல் அடிப்படையிலான சிகிச்சை மற்றும் நீண்டகால நோய் மேலாணிமைக்கு முக்கியத்துவம் அளிக்கப்படுகிறது.
        </Tam>
      </div>
    </div>
  </section>
);

/* ---------------- Services ---------------- */
const SERVICES = [
  { en: 'General Medicine', ta: 'பொது மருத்துவம்', den: 'Comprehensive evaluation and treatment of common medical conditions.', dta: 'பொதுவான மருத்துவப் பிரச்சினைகளுக்கான முழுமையான பரிசோதனை மற்றும் சிகிச்சை.', Icon: Stethoscope },
  { en: 'Diabetes Care', ta: 'சர்க்கரை நோய் பராமரிப்பு', den: 'Diagnosis, treatment and long-term management of diabetes.', dta: 'சர்க்கரை நோயைக் கண்டறிதல், சிகிச்சை மற்றும் நீண்டகால மேலாண்மை.', Icon: Droplets },
  { en: 'Hypertension & Cardiovascular Risk', ta: 'உயர் ரத்த அழுத்தம் & இதய நோய் அபாய மதிப்பீடு', den: 'Blood pressure management and cardiovascular risk assessment.', dta: 'ரத்த அழுத்தக் கட்டுப்பாடு மற்றும் இதய நோய் அபாய மதிப்பீடு.', Icon: HeartPulse },
  { en: 'Thyroid & Metabolic Disorders', ta: 'தைராய்டு & வளர்சிதை மாற்றக் கோளாறுகள்', den: 'Evaluation and management of thyroid disorders, obesity and metabolic conditions.', dta: 'தைராய்டு நோய்கள், உடல் பருமன் மற்றும் வளர்சிதை மாற்றக் கோளாறுகளுக்கான மதிப்பீடு மற்றும் சிகிச்சை.', Icon: Activity },
  { en: 'Fever & Infections', ta: 'காய்ச்சல் & தொற்றுநோய்கள்', den: 'Evaluation and treatment of fever and common infections.', dta: 'காய்ச்சல் மற்றும் பொதுவான தொற்றுநோய்களுக்கான பரிசோதனை மற்றும் சிகிச்சை.', Icon: Thermometer },
  { en: 'Allergy & Asthma', ta: 'ஒவ்வாமை & ஆஸ்துமா', den: 'Care for asthma, allergies, cough and breathing-related problems.', dta: 'ஆஸ்துமா, ஒவ்வாமை, இருமல் மற்றும் சுவாசப் பிரச்சினைகளுக்கான மருத்துவ சேவை.', Icon: Wind },
  { en: 'Skin Conditions', ta: 'தோல் நோய்கள்', den: 'Evaluation and treatment of common skin conditions.', dta: 'பொதுவான தோல் நோய்களுக்கான பரிசோதனை மற்றும் சிகிச்சை.', Icon: Fingerprint },
  { en: 'Obesity Management', ta: 'உடல் பருமன் மேலாண்மை', den: 'Personalized medical and lifestyle-based weight management.', dta: 'மருத்துவ மற்றும் வாழ்க்கைமுறை மாற்றங்களின் மூலம் தனிப்பட்ட உடல் எடை மேலாண்மை.', Icon: Scale },
  { en: 'Wound & Diabetic Foot Care', ta: 'காயங்கள் & சர்க்கரை நோய் பாத பராமரிப்பு', den: 'Chronic wound and diabetic foot ulcer assessment and care.', dta: 'நீண்டகால காயங்கள் மற்றும் சர்க்கரை நோய் பாதப் புண்களுக்கான மதிப்பீடு மற்றும் சிகிச்சை.', Icon: Bandage },
];

const MINOR_PROCEDURES = [
  { en: 'Wound Suturing', ta: 'காயங்களுக்கு தையல் போடுதல்' },
  { en: 'Abscess Drainage', ta: 'சீழ்க்கட்டி திறந்து சீழ் அகற்றுதல்' },
  { en: 'Ear Lobe Repair', ta: 'காது மடல் சீரமைப்பு' },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="py-16 md:py-24 bg-slate-50/70 scroll-mt-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="max-w-2xl">
        <p className="text-green-700 font-bold text-xs tracking-[0.18em] uppercase">Services</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Our Medical Services</h2>
        <Tam className="block mt-2 text-lg text-green-700 font-semibold">நாங்கள் வழங்கும் மருத்துவ சேவைகள்</Tam>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Comprehensive care for common medical conditions, chronic diseases and selected minor procedures.
        </p>
        <Tam className="block mt-1.5 text-slate-500 text-sm leading-relaxed">
          பொதுவான மருத்துவப் பிரச்சினைகள், நீண்டகால நோய்கள் மற்றும் தேர்ந்தெடுக்கப்பட்ட சிறிய மருத்துவச் சிகிச்சைகளுக்கான முழுமையான சேவை.
        </Tam>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map(({ en, ta, den, dta, Icon }, i) => (
          <article key={en} data-testid={`service-card-${i}`}
            className="group rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm hover:shadow-md hover:border-green-700/30 transition-all duration-200">
            <div className="h-11 w-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center group-hover:bg-green-700 group-hover:text-white transition-colors">
              <Icon className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <h3 className="mt-4 font-bold text-navy text-[17px] leading-snug">{en}</h3>
            <Tam className="block text-green-700 text-sm font-semibold mt-0.5 leading-snug">{ta}</Tam>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{den}</p>
            <Tam className="block mt-1.5 text-[13px] text-slate-500 leading-relaxed">{dta}</Tam>
          </article>
        ))}

        {/* Minor procedures special card */}
        <article data-testid="service-card-minor-procedures"
          className="rounded-2xl bg-navy text-white p-6 shadow-sm">
          <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center">
            <Scissors className="h-5 w-5" strokeWidth={1.8} />
          </div>
          <h3 className="mt-4 font-bold text-[17px]">Minor Procedures</h3>
          <Tam className="block text-green-300 text-sm font-semibold mt-0.5">சிறிய மருத்துவச் சிகிச்சை முறைகள்</Tam>
          <ul className="mt-4 space-y-3">
            {MINOR_PROCEDURES.map(p => (
              <li key={p.en} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-green-300 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold">{p.en}</p>
                  <Tam className="block text-[12px] text-white/70">{p.ta}</Tam>
                </div>
              </li>
            ))}
          </ul>
        </article>
      </div>
    </div>
  </section>
);

/* ---------------- Conditions ---------------- */
const CONDITIONS = [
  ['Diabetes', 'சர்க்கரை நோய்'],
  ['Hypertension', 'உயர் ரத்த அழுத்தம்'],
  ['Thyroid Disorders', 'தைராய்டு நோய்கள்'],
  ['Anaemia', 'இரத்த சோகை'],
  ['Dyslipidaemia', 'கொழுப்பு சத்து அதிகரிப்பு'],
  ['Obesity', 'உடல் பருமன்'],
  ['Fever & Infections', 'காய்ச்சல் & தொற்றுநோய்கள்'],
  ['Asthma', 'ஆஸ்துமா'],
  ['Allergy', 'ஒவ்வாமை'],
  ['Skin Disorders', 'தோல் நோய்கள்'],
  ['Chronic Wounds', 'நீண்டகால காயங்கள்'],
  ['Diabetic Foot Ulcers', 'சர்க்கரை நோய் பாதப் புண்கள்'],
];

const Conditions = () => (
  <section data-testid="conditions-section" className="py-16 md:py-20 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="max-w-2xl">
        <h2 className="text-3xl sm:text-4xl font-bold text-navy tracking-tight">Conditions We Commonly Treat</h2>
        <Tam className="block mt-2 text-lg text-green-700 font-semibold">நாங்கள் பொதுவாக சிகிச்சை அளிக்கும் நோய்கள்</Tam>
      </div>
      <div className="mt-8 flex flex-wrap gap-2.5">
        {CONDITIONS.map(([en, ta], i) => (
          <div key={en} data-testid={`condition-pill-${i}`}
            className="inline-flex flex-col rounded-full border border-slate-200 bg-slate-50/60 px-5 py-2 hover:border-green-700/40 hover:bg-green-50/50 transition-colors">
            <span className="text-sm font-semibold text-navy leading-tight">{en}</span>
            <Tam className="text-[11px] text-slate-500 leading-tight">{ta}</Tam>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Doctor ---------------- */
const DoctorSection = () => (
  <section id="doctor" data-testid="doctor-section" className="py-16 md:py-24 bg-slate-50/70 scroll-mt-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="grid md:grid-cols-5 gap-10 md:gap-14 items-center">
        <div className="md:col-span-2 relative">
          <div className="absolute -inset-4 rounded-[32px] bg-green-700/5" aria-hidden="true" />
          <div className="relative rounded-[28px] overflow-hidden border border-slate-200 bg-white shadow-[0_20px_50px_rgba(22,52,107,0.12)]">
            <img src={DOCTOR_PHOTO} alt="Dr. K. Mouhamed Nazar — General Physician in Koliyanur"
              data-testid="doctor-profile-photo"
              className="w-full object-cover object-top aspect-[4/5]" />
            <div className="absolute bottom-0 inset-x-0 bg-navy/95 backdrop-blur px-5 py-4">
              <p className="font-bold text-white">Dr. K. Mouhamed Nazar</p>
              <p className="text-green-300 text-xs font-semibold mt-0.5">MBBS., MD (General Medicine), M.Diab.</p>
            </div>
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="text-green-700 font-bold text-xs tracking-[0.18em] uppercase">Your Doctor</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Meet Your Doctor</h2>
          <Tam className="block mt-2 text-lg text-green-700 font-semibold">உங்கள் மருத்துவரை அறிந்து கொள்ளுங்கள்</Tam>

          <div className="mt-7 space-y-4">
            <div className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm">
              <div className="h-10 w-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
                <UserRound className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-navy">Dr. K. Mouhamed Nazar, MBBS., MD (General Medicine), M.Diab.</p>
                <p className="text-slate-600 text-sm mt-0.5">Assistant Professor, Dept. of General Medicine</p>
                <p className="text-slate-500 text-[12px] mt-0.5 leading-relaxed">Mahatma Gandhi Medical College and Research Institute, Pondicherry - Cuddalore Rd, ECR, Pillayarkuppam, Puducherry 607403</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm">
              <div className="h-10 w-10 rounded-xl bg-green-700/10 text-green-700 flex items-center justify-center shrink-0">
                <HeartPulse className="h-5 w-5" />
              </div>
              <div>
                <p className="text-slate-700 leading-relaxed">
                  Dedicated to providing comprehensive, compassionate and evidence-based medical care.
                </p>
                <Tam className="block mt-1.5 text-slate-500 text-sm leading-relaxed">
                  முழுமையான, அக்கறையுடனும் அறிவியல் அடிப்படையிலும் கூடிய மருத்துவ சேவையை வழங்குவதில் அர்ப்பணிப்புடன் செயல்படுகிறார்.
                </Tam>
              </div>
            </div>

            {/* In-Patient Treatment At */}
            <div className="rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm" data-testid="inpatient-hospitals-card">
              <p className="font-bold text-navy text-sm uppercase tracking-wider flex items-center gap-2">
                <MapPin className="h-4 w-4 text-green-700" /> In-Patient Treatment At
              </p>
              <ol className="mt-4 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-navy text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <div>
                    <a href={MEDWAY_MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="hospital-link-medway"
                      className="font-semibold text-navy text-sm hover:text-green-700 hover:underline underline-offset-2 inline-flex items-center gap-1">
                      Medway Hospital <MapPin className="h-3.5 w-3.5 text-green-700" />
                    </a>
                    <p className="text-slate-500 text-[13px] leading-relaxed">15, Ranganathan St, K K Nagar, Chairman Chidam, Viluppuram, Tamil Nadu 605602</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-navy text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <div>
                    <a href={MGMCRI_MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="hospital-link-mgmcri"
                      className="font-semibold text-navy text-sm hover:text-green-700 hover:underline underline-offset-2 inline-flex items-center gap-1">
                      Mahatma Gandhi Medical College and Research Institute <MapPin className="h-3.5 w-3.5 text-green-700" />
                    </a>
                    <p className="text-slate-500 text-[13px] leading-relaxed">Pondicherry - Cuddalore Rd, ECR, Pillayarkuppam, Puducherry 607403</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Google Reviews ---------------- */
const StarRow = ({ value = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(i => (
      <Star key={i} className={`h-4 w-4 ${i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
    ))}
  </div>
);

const GoogleReviews = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/google-reviews`)
      .then(r => setData(r.data))
      .catch(() => setData({ fallback: true, reason: 'unavailable', reviews: [] }));
  }, []);

  // Loading placeholder (keeps layout stable)
  if (!data) {
    return (
      <section data-testid="google-reviews-section" className="py-14 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 animate-pulse">
          <div className="h-5 w-40 bg-slate-100 rounded mb-3" />
          <div className="h-8 w-72 bg-slate-100 rounded" />
        </div>
      </section>
    );
  }

  // Fallback: real rating badge from the live Google listing (no fabricated reviews)
  if (data.fallback || !data.reviews?.length) {
    return (
      <section data-testid="google-reviews-section" className="py-14 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <a href={MAPS_LISTING_URL} target="_blank" rel="noopener noreferrer" data-testid="google-rating-badge"
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 rounded-2xl border border-slate-200 bg-slate-50/60 px-6 py-7 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <svg viewBox="0 0 24 24" className="h-6 w-6"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-navy">5.0</span>
                  <StarRow value={5} />
                </div>
                <p className="text-sm text-slate-600 mt-0.5">Rated on Google by our patients</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 text-navy font-semibold px-5 py-2 text-sm hover:bg-navy/5 transition-colors">
              Read reviews on Google <MapPin className="h-4 w-4" />
            </span>
          </a>
        </div>
      </section>
    );
  }

  // Live reviews from Places API
  return (
    <section data-testid="google-reviews-section" className="py-14 md:py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-green-700 font-bold text-xs tracking-[0.18em] uppercase">Patient Feedback</p>
            <h2 className="mt-2 text-3xl font-bold text-navy tracking-tight">What Patients Say on Google</h2>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-3xl font-bold text-navy">{data.rating?.toFixed(1)}</span>
              <div>
                <StarRow value={data.rating || 5} />
                <p className="text-xs text-slate-500 mt-0.5">{data.user_ratings_total} Google reviews</p>
              </div>
            </div>
          </div>
          <a href={data.google_maps_uri || MAPS_LISTING_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-green-700 hover:underline">
            View all on Google Maps <MapPin className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.reviews.slice(0, 3).map((rev, i) => (
            <figure key={i} data-testid={`google-review-${i}`}
              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 shadow-sm flex flex-col">
              <StarRow value={rev.rating || 5} />
              <blockquote className="mt-3 text-sm text-slate-700 leading-relaxed flex-1">
                “{rev.text?.length > 220 ? rev.text.slice(0, 220) + '…' : rev.text}”
              </blockquote>
              <figcaption className="mt-4 text-xs text-slate-500">
                {rev.author_uri ? (
                  <a href={rev.author_uri} target="_blank" rel="noopener noreferrer" className="font-semibold text-navy hover:underline">{rev.author_name}</a>
                ) : (
                  <span className="font-semibold text-navy">{rev.author_name}</span>
                )}
                {rev.relative_time ? ` · ${rev.relative_time}` : ''}
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="mt-5 text-[11px] text-slate-400" translate="no">Reviews sourced from Google Maps, sorted by relevance.</p>
      </div>
    </section>
  );
};

/* ---------------- Patient journey ---------------- */
const JOURNEY = [
  { n: '01', en: 'Book', ta: 'முன்பதிவு', Icon: CalendarDays },
  { n: '02', en: 'Consult', ta: 'மருத்துவர் ஆலோசனை', Icon: Stethoscope },
  { n: '03', en: 'Evaluate & Treat', ta: 'பரிசோதனை & சிகிச்சை', Icon: ClipboardList },
  { n: '04', en: 'Follow-up', ta: 'தொடர்ச்சியான கண்காணிப்பு', Icon: HeartPulse },
];

const Journey = () => (
  <section data-testid="journey-section" className="py-16 md:py-20 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <h2 className="text-3xl sm:text-4xl font-bold text-navy tracking-tight text-center">Your Visit, Simplified</h2>
      {/* Desktop horizontal */}
      <div className="mt-12 hidden md:grid grid-cols-4 gap-6 relative">
        <div className="absolute top-7 left-[12%] right-[12%] h-px bg-slate-200" aria-hidden="true" />
        {JOURNEY.map(({ n, en, ta, Icon }, i) => (
          <div key={n} data-testid={`journey-step-${i}`} className="relative text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-navy text-white flex items-center justify-center shadow-md shadow-navy/20 relative z-10">
              <Icon className="h-6 w-6" strokeWidth={1.8} />
            </div>
            <p className="mt-4 text-xs font-bold tracking-[0.2em] text-green-700">{n}</p>
            <p className="mt-1 font-bold text-navy">{en}</p>
            <Tam className="block text-sm text-slate-500 mt-0.5">{ta}</Tam>
          </div>
        ))}
      </div>
      {/* Mobile vertical */}
      <div className="mt-10 md:hidden space-y-0 relative">
        <div className="absolute top-2 bottom-8 left-7 w-px bg-slate-200" aria-hidden="true" />
        {JOURNEY.map(({ n, en, ta, Icon }, i) => (
          <div key={n} data-testid={`journey-step-mobile-${i}`} className="relative flex items-start gap-4 pb-8">
            <div className="h-14 w-14 rounded-2xl bg-navy text-white flex items-center justify-center shadow-md shadow-navy/20 relative z-10 shrink-0">
              <Icon className="h-6 w-6" strokeWidth={1.8} />
            </div>
            <div className="pt-1.5">
              <p className="text-xs font-bold tracking-[0.2em] text-green-700">{n}</p>
              <p className="font-bold text-navy">{en}</p>
              <Tam className="block text-sm text-slate-500">{ta}</Tam>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- CTA ---------------- */
const CTA = () => (
  <section data-testid="cta-section" className="bg-navy relative overflow-hidden">
    <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-green-700/20 blur-3xl" aria-hidden="true" />
    <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/5 blur-3xl" aria-hidden="true" />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center relative">
      <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">Your Health Deserves Attention.</h2>
      <Tam className="block mt-2 text-xl text-green-300 font-semibold">உங்கள் ஆரோக்கியத்திற்கு உரிய கவனம் அளியுங்கள்.</Tam>
      <p className="mt-5 text-white/80 leading-relaxed max-w-2xl mx-auto">
        Whether you need treatment for a new illness or regular follow-up for a chronic condition, we are here to help.
      </p>
      <Tam className="block mt-2 text-white/60 text-sm leading-relaxed max-w-2xl mx-auto">
        புதிய உடல்நலப் பிரச்சினைக்கான சிகிச்சையாக இருந்தாலும், நீண்டகால நோய்க்கான தொடர்ச்சியான கண்காணிப்பாக இருந்தாலும், உங்களுக்கு தேவையான மருத்துவ சேவையை வழங்க நாங்கள் தயாராக இருக்கிறோம்.
      </Tam>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <a href={PHONE_TEL} data-testid="cta-book-appointment"
          className="inline-flex items-center gap-2 rounded-full bg-green-700 hover:bg-green-600 text-white font-semibold px-8 py-3.5 shadow-lg shadow-green-900/30 transition-colors">
          <CalendarDays className="h-5 w-5" /> Book an Appointment
        </a>
        <a href={PHONE_TEL} data-testid="cta-call-button"
          className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white hover:bg-white/10 font-semibold px-8 py-3.5 transition-colors">
          <Phone className="h-5 w-5" /> Call {PHONE}
        </a>
      </div>
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-16 md:py-24 bg-white scroll-mt-20">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="max-w-2xl">
        <p className="text-green-700 font-bold text-xs tracking-[0.18em] uppercase">Visit Us</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-navy tracking-tight">Visit K.M. Multi Speciality Clinic</h2>
        <Tam className="block mt-2 text-lg text-green-700 font-semibold">K.M. Multi Speciality Clinic-க்கு வருக</Tam>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-8 items-stretch">
        {/* Info card */}
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8 shadow-sm flex flex-col">
          <div className="space-y-6 flex-1">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-navy text-sm uppercase tracking-wide">Address</p>
                <p className="mt-1.5 text-slate-700 leading-relaxed">{ADDRESS_EN}</p>
                <Tam className="block mt-1.5 text-slate-500 text-sm leading-relaxed">{ADDRESS_TA}</Tam>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-green-700/10 text-green-700 flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-navy text-sm uppercase tracking-wide">Phone</p>
                <a href={PHONE_TEL} data-testid="contact-phone-link" className="mt-1.5 inline-block text-green-700 font-bold text-lg hover:underline">{PHONE}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="font-bold text-navy text-sm uppercase tracking-wide">Consultation Hours</p>
                <p className="mt-1.5 text-slate-700">Monday – Saturday · 6:00 PM – 10:30 PM</p>
                <p className="text-slate-500 text-sm">Sunday Holiday</p>
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap gap-2.5">
            <a href={PHONE_TEL} data-testid="contact-call-now"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-white font-semibold px-5 py-2.5 text-sm hover:bg-navy-dark transition-colors">
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-get-directions"
              className="inline-flex items-center gap-2 rounded-full bg-green-700 text-white font-semibold px-5 py-2.5 text-sm hover:bg-green-800 transition-colors">
              <MapPin className="h-4 w-4" /> Get Directions
            </a>
            <a href={PHONE_TEL} data-testid="contact-book-appointment"
              className="inline-flex items-center gap-2 rounded-full border-2 border-navy/20 text-navy font-semibold px-5 py-2.5 text-sm hover:bg-navy/5 transition-colors">
              <CalendarDays className="h-4 w-4" /> Book Appointment
            </a>
          </div>
        </div>

        {/* Map */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm min-h-[320px] bg-slate-100 relative">
          <iframe
            title="K.M. Multi Speciality Clinic location — Koliyanur, Tamil Nadu"
            data-testid="contact-map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d62458.73130232678!2d79.49938705795716!3d11.927989392051055!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5357714badb7a3%3A0x5250ae817d764007!2sK.M%20Multi%20Speciality%20Clinic!5e0!3m2!1sen!2sin!4v1791439970761!5m2!1sen!2sin"
            className="absolute inset-0 w-full h-full"
            loading="lazy"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Site Metrics (At a Glance) ---------------- */
const METRICS = [
  { num: '10+', en: 'Medical Services', ta: 'மருத்துவச் சேவைகள்', Icon: Stethoscope },
  { num: '6', en: 'Operating Days / Week', ta: 'வாரத்திற்கான பணி நாட்கள்', Icon: CalendarDays },
  { num: '4.5 hrs', en: 'Daily Consultation', ta: 'தினசரி ஆலோசனை நேரம்', Icon: Clock },
  { num: '2', en: 'In-Patient Hospitals', ta: 'உள்நோயாளி மருத்துவமனைகள்', Icon: Building2 },
  { num: 'EN / தமிழ்', en: 'Languages Spoken', ta: 'பேசும் மொழிகள்', Icon: Languages },
];

const Metrics = () => (
  <section data-testid="metrics-section" className="py-14 md:py-16 bg-slate-50/70 border-t border-slate-100">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="max-w-2xl mb-8">
        <p className="text-green-700 font-bold text-xs tracking-[0.18em] uppercase">At a Glance</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-navy tracking-tight">K.M. Clinic by the Numbers</h2>
        <Tam className="block mt-1.5 text-base text-green-700 font-semibold">K.M. சிகிச்சை மையம் — ஒரு பார்வையில்</Tam>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
        {METRICS.map(({ num, en, ta, Icon }, i) => (
          <div key={en} data-testid={`metric-${i}`}
            className="rounded-2xl bg-white border border-slate-200/80 p-5 text-center shadow-sm hover:shadow-md hover:border-green-700/30 transition-all">
            <div className="h-10 w-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center mx-auto">
              <Icon className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <p className="mt-3 text-2xl font-bold text-navy tracking-tight">{num}</p>
            <p className="mt-1 text-[13px] font-semibold text-slate-700 leading-tight">{en}</p>
            <Tam className="block text-[11px] text-slate-500 mt-0.5 leading-tight">{ta}</Tam>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Mobile sticky bar ---------------- */
const MobileBar = () => (
  <nav data-testid="mobile-action-bar" aria-label="Quick actions"
    className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(22,52,107,0.10)]">
    <div className="grid grid-cols-3">
      <a href={PHONE_TEL} data-testid="mobilebar-call" className="flex flex-col items-center gap-1 py-2.5 text-navy active:bg-slate-50">
        <Phone className="h-5 w-5" /><span className="text-[11px] font-semibold">Call</span>
      </a>
      <a href={PHONE_TEL} data-testid="mobilebar-appointment" className="flex flex-col items-center gap-1 py-2.5 text-navy active:bg-slate-50">
        <CalendarDays className="h-5 w-5" /><span className="text-[11px] font-semibold">Appointment</span>
      </a>
      <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" data-testid="mobilebar-directions" className="flex flex-col items-center gap-1 py-2.5 text-green-700 active:bg-slate-50">
        <MapPin className="h-5 w-5" /><span className="text-[11px] font-semibold">Directions</span>
      </a>
    </div>
  </nav>
);

/* ---------------- Footer ---------------- */
const Footer = () => (
  <footer data-testid="site-footer" className="bg-navy-dark text-white pb-24 md:pb-0">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="K.M. Multi Speciality Clinic logo" className="h-12 w-12 object-contain rounded-lg bg-white p-1" />
            <div>
              <p className="font-bold text-sm">K.M. MULTI SPECIALITY CLINIC</p>
              <Tam className="block text-green-300 text-xs">K.M. சிறப்பு சிகிச்சை மையம்</Tam>
            </div>
          </div>
          <p className="mt-4 text-green-300 font-semibold text-sm">
            Care Beyond Treatment <span className="text-white/30 mx-1">|</span> <Tam>சிகிச்சையுடன், உங்கள் முழுமையான நலனிலும் அக்கறை</Tam>
          </p>
        </div>
        <div>
          <p className="font-bold text-sm uppercase tracking-wider text-white/70">Quick Links</p>
          <nav className="mt-4 flex flex-wrap gap-x-5 gap-y-2" aria-label="Footer">
            {NAV_LINKS.map(l => (
              <button key={l.id} data-testid={`footer-nav-${l.id}`}
                onClick={() => document.getElementById(l.id)?.scrollIntoView({ behavior: 'smooth' })}
                className="text-sm text-white/80 hover:text-white transition-colors">
                {l.en}
              </button>
            ))}
          </nav>
        </div>
        <div>
          <p className="font-bold text-sm uppercase tracking-wider text-white/70">Contact</p>
          <a href={PHONE_TEL} data-testid="footer-phone" className="mt-4 flex items-center gap-2 text-white/90 hover:text-white text-sm">
            <Phone className="h-4 w-4 text-green-300" /> {PHONE}
          </a>
          <p className="mt-3 flex items-start gap-2 text-white/70 text-sm leading-relaxed">
            <MapPin className="h-4 w-4 text-green-300 mt-0.5 shrink-0" /> {ADDRESS_EN}
          </p>
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-white/10">
        <p className="text-[12px] text-white/50 leading-relaxed">
          Medical Disclaimer: Information provided on this website is for general informational purposes and does not replace professional medical consultation.
        </p>
        <Tam className="block mt-1.5 text-[12px] text-white/40 leading-relaxed">
          மருத்துவ அறிவிப்பு: இந்த இணையதளத்தில் வழங்கப்படும் தகவல்கள் பொதுவான தகவல்களுக்காக மட்டுமே. இது நேரடி மருத்துவர் ஆலோசனைக்கு மாற்றாகாது.
        </Tam>
        <p className="mt-5 text-[12px] text-white/60">© K.M. Multi Speciality Clinic. All Rights Reserved.</p>
      </div>
    </div>
  </footer>
);

/* ---------------- Page ---------------- */
export default function PublicWebsite() {
  return (
    <div className="min-h-screen bg-white font-website text-slate-800 antialiased">
      <Header />
      <main>
        <Hero />
        <TrustStrip />
        <About />
        <Services />
        <Conditions />
        <DoctorSection />
        <GoogleReviews />
        <Journey />
        <CTA />
        <Contact />
      </main>
      <Metrics />
      <Footer />
      <MobileBar />
    </div>
  );
}
